import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import classNames from 'classnames';
import Heading from 'shared/components/common/heading/Heading';
import CustomInput from 'shared/components/common/custom_input/CustomInput';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import { useSelector } from 'react-redux';
import { AddSubCategoryMutation, UpdateSubCategoryMutation } from 'shared/services/sub_category_service/SubCategoryService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { SubCategoryVS } from 'shared/utils/validation/AuthValidation';
import { useFormik } from 'formik';
import CustomSelect from 'shared/components/common/custom_selection/CustomSelection';
import { GetAllCategoryQuery } from 'shared/services/category_service/CategoryService';
import { toastMessage } from 'shared/components/toast/Toast';
import UploadAvatar from 'shared/components/common/upload_avatar/UploadAvatar';
import { uploadFileMedia } from 'shared/services/media_service/Media_Service';

export default function AddSubCategory({ show, datatable, handleClose, isEdit = false, editData = null, selectedCategory, onSuccessAdd }) {
    const { mutate: addSubCategoryMutate, isLoading: SubCategoryLoading, isSuccess: SubCategoryIsSuccess } = AddSubCategoryMutation();
    const { mutate: updateSubCategoryMutate, isLoading: UpdateSubCategoryLoading, isSuccess: UpdateSubCategoryIsSuccess } = UpdateSubCategoryMutation();
    const { data: listCategory } = GetAllCategoryQuery();
    const {
        theme: { theme }
    } = useSelector((state) => state.root);
    
    // Image upload states
    const [avatarFile, setAvatarFile] = useState(null);
    const [selectedImg, setSelectedImg] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        listCategory?.map((ii) => {
            ii['label'] = ii?.name;
            return ii;
        });
    }, [listCategory]);

    useEffect(() => {
        if ((SubCategoryIsSuccess || UpdateSubCategoryIsSuccess) && show) {
            if (onSuccessAdd) onSuccessAdd(values.subname);
            setTimeout(() => {
                onClose();
            }, 100);
        }
    }, [SubCategoryIsSuccess, UpdateSubCategoryIsSuccess]);

    const onClose = () => {
        handleClose();
        formik.resetForm();
        setAvatarFile(null);
        setSelectedImg('');
        setIsSubmitting(false);
    };

    const initialValues = {
        subname: isEdit && editData?.name ? editData.name : '',
        category: isEdit && editData?.category ? { ...editData.category, label: editData.category.name } : (selectedCategory || ''),
        point: isEdit && editData?.point !== undefined ? editData.point : ''
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: SubCategoryVS,
        onSubmit: (value) => {
            categoryHandler(value);
        }
    });
    const { handleChange, setFieldValue, handleSubmit, values, touched, errors } = formik;

    useEffect(() => {
        if (show && isEdit && editData) {
            if (editData.image) {
                setSelectedImg(editData.image);
                setAvatarFile(editData.image);
            }
        }
    }, [show, isEdit, editData]);

    useEffect(() => {
        if (show && !isEdit && selectedCategory) {
            setFieldValue('category', selectedCategory);
        }
    }, [show, isEdit, selectedCategory, setFieldValue]);

    const uploadMedia = async () => {
        try {
            let fileBody = new FormData();
            fileBody.append('image', avatarFile);
            return await uploadFileMedia(fileBody);
        } catch (error) {
            console.error("Image upload failed", error);
            return null;
        }
    };

    const categoryHandler = async (values) => {
        const isDuplicate = datatable?.some(
            (subcategory) =>
                subcategory._id !== editData?._id &&
                subcategory.name.trim().toLowerCase() === values.subname.trim().toLowerCase() &&
                subcategory.category?._id === values.category?._id
        );

        if (isDuplicate) {
            toastMessage('error', `Sub Category "${values.subname}" already exists for this category!`);
            return;
        }

        setIsSubmitting(true);
        try {
            let imageUrl = '';
            if (avatarFile && typeof avatarFile !== 'string') {
                const resp = await uploadMedia();
                if (resp?.data) {
                    imageUrl = resp.data.url;
                }
            } else if (typeof avatarFile === 'string') {
                imageUrl = avatarFile;
            }

            if (isEdit && editData?._id) {
                updateSubCategoryMutate({
                    id: editData._id,
                    payload: {
                        name: values.subname,
                        category: values.category?._id,
                        point: values.point,
                        image: imageUrl
                    }
                }, {
                    onSettled: () => {
                        setIsSubmitting(false);
                    }
                });
            } else {
                addSubCategoryMutate({
                    name: values.subname,
                    category: values.category?._id,
                    point: values.point,
                    image: imageUrl
                }, {
                    onSettled: () => {
                        setIsSubmitting(false);
                    }
                });
            }
        } catch (error) {
            console.error(error);
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header className={styles.Header}>
                <Heading heading={isEdit ? 'Edit Sub Category' : 'Add Sub Category'} headingStyle={styles.AddLinkHeading} />
            </Modal.Header>
            <Modal.Body className={classNames(`${theme}ModalBackground`, styles.Body)}>
                <UploadAvatar UploadedFile={avatarFile} setFile={setAvatarFile} selectedImg={selectedImg} setSelectedImg={setSelectedImg} />
                <div className={classNames(styles.AddLinkContainer)}>
                    <CustomSelect
                        label={'Category'}
                        placeholder={'Select Category'}
                        options={listCategory || []}
                        disabled={listCategory ? false : true}
                        value={values.category}
                        onChangeHandle={async (item) => {
                            setFieldValue('category', item);
                        }}
                        error={errors.category && touched.category ? errors.category : ''}
                        MainContainerStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                    />
                    <CustomInput
                        label={isEdit ? 'Edit Sub Category Name' : 'Add Sub Category'}
                        placeholder={'e.g. Sub Category'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        value={values.subname}
                        onChange={handleChange('subname')}
                        error={errors.subname && touched.subname ? errors.subname : ''}
                    />
                    <CustomInput
                        label={'Category Points'}
                        placeholder={'e.g. 12'}
                        type={'number'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        value={values.point}
                        onChange={handleChange('point')}
                        error={errors.point && touched.point ? errors.point : ''}
                    />

                    <div className="d-flex justify-content-end">
                        <CustomButton text={'Cancel'} containerStyle={styles.CancelBtn} submitHandle={onClose} />
                        <CustomButton text={isEdit ? 'Update' : 'Add'} containerStyle={styles.SubmitBtn} submitHandle={handleSubmit} />
                    </div>
                </div>
                {(isSubmitting || SubCategoryLoading || UpdateSubCategoryLoading) && <OverlayLoading />}
            </Modal.Body>
        </Modal>
    );
}
